import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { fetchPublicCustomPage } from '@/hooks/useCustomPages';
import type { MerchantCustomPage } from '@/lib/customPages';
import TemplateRenderer from '@/components/feature/TemplateRenderer';

/**
 * Page personnalisée publique d'une boutique — route /p/:slug.
 * Le contenu est rendu par le moteur de balises (TemplateRenderer), donc
 * les {{site_name}}, {{products}}, {{hebergements}}… fonctionnent.
 */
export default function CustomPage() {
  const { slug } = useParams<{ slug: string }>();
  const { tenant } = useTenant();
  const merchantId = tenant ? Number(tenant.id) : null;

  const [page, setPage] = useState<MerchantCustomPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!merchantId || !slug) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchPublicCustomPage(merchantId, slug)
      .then((result) => {
        if (!cancelled) setPage(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [merchantId, slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-lg font-bold font-heading text-foreground-950">Erreur de chargement</h2>
        <p className="text-sm text-foreground-500 mt-2">Impossible de charger cette page pour le moment.</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-lg font-bold font-heading text-foreground-950">Page introuvable</h2>
        <p className="text-sm text-foreground-500 mt-2">Cette page n&apos;existe pas ou n&apos;est plus disponible.</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-6 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium no-underline whitespace-nowrap">
          <i className="ri-home-line"></i>
          Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  if (!page.published) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-eye-off-line text-2xl text-secondary-600"></i>
        </div>
        <h2 className="text-lg font-bold font-heading text-foreground-950">Page en brouillon</h2>
        <p className="text-sm text-foreground-500 mt-2">
          Cette page n&apos;est pas encore publiée par le marchand.
        </p>
      </div>
    );
  }

  const content = page.content || '';
  // Contenu « page complète » : il gère déjà ses propres sections/hero,
  // on l'affiche en pleine largeur. Sinon, on l'habille (hero + prose).
  const looksLikeFullPage = /<(section|header|footer|article)[\s>]|class=["'][^"']*\b(section|hero|container)\b/i.test(content);

  return (
    <div className="w-full">
      {!looksLikeFullPage && (
        <header className="relative overflow-hidden border-b border-background-200/70">
          <div className="absolute inset-0 bg-gradient-to-b from-background-100/80 to-background-50"></div>
          <div className="relative max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
            <nav className="flex items-center justify-center gap-1.5 text-xs text-foreground-400 mb-4 flex-wrap">
              <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 truncate max-w-[60vw]">{page.title}</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight">{page.title}</h1>
            <span className="block w-16 h-1 rounded-full bg-primary-500 mx-auto mt-6"></span>
          </div>
        </header>
      )}

      {looksLikeFullPage ? (
        <TemplateRenderer content={content} />
      ) : (
        <article className="zf-prose max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <TemplateRenderer content={content} />
        </article>
      )}
    </div>
  );
}